import { GridCellParams } from '@mui/x-data-grid-pro';
import { addressComponentColumns } from '../../Compounds/UpstackDataGrid/addressColumns';
import { GroupedGridColDef } from '../../Compounds/UpstackDataGrid/gridColumnTypes';
import { standardOperators } from '../../Compounds/UpstackDataGrid/helpers';
import { LocationRow } from '../../../Api/Locations/locationsApi';
import { DetailCTARenderer } from '../../Compounds/UpstackDataGrid/CellRenderers/DetailCTARenderer';
import { LOCATION_KEY_PARAM } from './LocationDrawer';
import { BILLING_ADDRESS_LABEL, ROLE_LABELS } from './locationRoleLabels';

export const LOCATIONS_GRID_KEY = 'inventory_locations_grid';
export const NO_COUNT = '—';

export interface LocationGridRow extends LocationRow {
  id: string;
  address: string;
  location_type: string;
}

const formatAddress = (row: LocationRow): string => {
  const stateAndZip = [row.state, row.zip].filter(Boolean).join(' ');
  return [row.street_address, row.city, stateAndZip, row.country].filter(Boolean).join(', ');
};

export const toLocationGridRows = (rows: LocationRow[]): LocationGridRow[] =>
  rows.map((row) => ({
    ...row,
    id: row.location_key,
    address: formatAddress(row),
    location_type:
      row.address_source === 'billing'
        ? BILLING_ADDRESS_LABEL
        : row.roles.map((role) => ROLE_LABELS[role] ?? role).join(' / ')
  }));

const textColumn = (field: string, headerName: string, width: number): GroupedGridColDef => ({
  field,
  headerName,
  sortable: true,
  width,
  filterable: true,
  type: 'singleSelect',
  valueOptions: [],
  filterOperators: standardOperators(),
  description: 'Drag to move column'
});

const countColumn = (field: keyof LocationRow, headerName: string): GroupedGridColDef => ({
  ...textColumn(field, headerName, 150),
  valueFormatter: (value: number | null | undefined) => value ?? NO_COUNT
});

export const locationsGridColumns = (projectsEnabled: boolean, rows: LocationRow[] = []): GroupedGridColDef[] => {
  const carriesCount = (field: keyof LocationRow) => !rows.length || rows.some((row) => row[field] !== undefined);

  return [
    {
      field: 'details',
      headerName: 'Details',
      sortable: false,
      width: 65,
      filterable: false,
      type: 'singleSelect',
      valueOptions: [],
      renderCell: (params: GridCellParams) => (
        <DetailCTARenderer
          data={params.row}
          value=""
          valueFormatted=""
          paramKey={LOCATION_KEY_PARAM}
          title={`Open details for ${params.row.location_name || params.row.address || params.row.location_key}`}
        />
      ),
      hideable: false,
      disableReorder: true
    },
    textColumn('location_code', 'Location Code', 150),
    textColumn('location_name', 'Location Name', 220),
    textColumn('address', 'Address', 320),
    ...addressComponentColumns(standardOperators()).map((column) => ({ ...column, defaultHidden: false })),
    textColumn('location_type', 'Location Type', 150),
    countColumn('active_inventory_count', 'Active Inventory'),
    ...(carriesCount('active_case_count') ? [countColumn('active_case_count', 'Active Cases')] : []),
    countColumn('active_order_count', 'Active Orders'),
    ...(projectsEnabled && carriesCount('active_project_count')
      ? [countColumn('active_project_count', 'Active Projects')]
      : [])
  ];
};
